import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { formatCardDate, counterBtn } from '../../components/shared/helpers';

// Partner party-size step (date → passengers → availability). Mirrors the
// customer SelectPassengers UI + the 20-seated cap, but runs on the isolated
// partner booking context and does NOT fetch availability here — the
// PartnerAvailability screen fetches on mount from the context's passengers,
// so this screen only sets the party and navigates (no double fetch).
const SEAT_CAP = 20;

export default function PartnerSelectPassengers() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const { selectedDate, passengers, setPassengers, fading, go, exitToDashboard } = usePartnerBooking();

  const [capNotice, setCapNotice] = useState(false);

  const seated = passengers.adults + passengers.children;

  // Guard — wait for the auth check, then require an approved partner session
  // and a selected date (otherwise there is nothing to size a party for).
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;
  if (selectedDate === null) return <Navigate to="/partner/book/date" replace />;

  const increment = (key, max) => {
    // Adults + children are limited to SEAT_CAP seated guests; infants are not.
    if (key !== 'infants' && seated >= SEAT_CAP) {
      setCapNotice(true);
      return;
    }
    setCapNotice(false);
    setPassengers(p => ({ ...p, [key]: Math.min(max, p[key] + 1) }));
  };

  const decrement = (key, min) => {
    setCapNotice(false);
    setPassengers(p => ({ ...p, [key]: Math.max(min, p[key] - 1) }));
  };

  return (
    <div className={`w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

          {/* Header row: back arrow → date · Exit to Dashboard */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => go('/partner/book/date')}
              className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
              aria-label="Go back"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {/* No hold on this step → exit immediately */}
            <button
              type="button"
              onClick={exitToDashboard}
              className="font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
            >
              Exit to Dashboard
            </button>
          </div>

          <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
            Partner Booking
          </p>
          <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1">
            Who's Joining?
          </h1>
          <p className="font-dm-sans text-sm text-white/55 font-light mb-5 leading-relaxed">
            {formatCardDate(selectedDate)}
          </p>

          {/* Passenger counters */}
          <div className="flex flex-col">
            {[
              { key: 'adults',   label: 'Adults',   sub: '13 years and above', min: 1, max: 20 },
              { key: 'children', label: 'Children', sub: '3 – 12 years',       min: 0, max: 20 },
              { key: 'infants',  label: 'Infants',  sub: 'Under 3 years',      min: 0, max: 10 },
            ].map(({ key, label, sub, min, max }, i, arr) => (
              <div
                key={key}
                className={`flex items-center justify-between py-3.5 ${i < arr.length - 1 ? 'border-b border-white/[0.08]' : ''}`}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="font-dm-sans text-[15px] font-light text-white">{label}</span>
                  <span className="font-dm-sans text-sm font-light text-white/60">{sub}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => decrement(key, min)}
                    aria-label={`Decrease ${label}`}
                    className={counterBtn}
                  >
                    -
                  </button>
                  <span className="w-6 text-center font-dm-sans text-[15px] font-light text-white tabular-nums">
                    {passengers[key]}
                  </span>
                  <button
                    type="button"
                    onClick={() => increment(key, max)}
                    aria-label={`Increase ${label}`}
                    className={counterBtn}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Seated-party cap notice */}
          {capNotice && (
            <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed mt-3">
              Online bookings support only a maximum of 20 guests per booking. For larger group bookings, please contact{' '}
              <a href="tel:+919854958549" className="text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200">9854958549</a>
              {' / '}
              <a href="tel:+919435558001" className="text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200">9435558001</a>.
            </p>
          )}

          {/* Live summary */}
          <p className="font-dm-sans text-sm font-medium text-[rgba(212,168,67,0.7)] mt-4 text-center tracking-[0.02em]">
            {`${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'} · ${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'} · ${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}`}
          </p>

          {/* Info note */}
          <div className="mt-3 border border-[rgba(255,255,255,0.12)] rounded-lg bg-[rgba(255,255,255,0.04)] px-4 py-3">
            <p className="font-dm-sans text-sm text-white/70 font-light leading-relaxed">
              Infants travel on a guardian's lap and do not occupy a seat. If your infant requires their own seat, please book a Children's ticket instead.
            </p>
          </div>

          {/* Check Availability — no fetch here; PartnerAvailability fetches on mount */}
          <button
            type="button"
            onClick={() => go('/partner/book/availability')}
            className="w-full mt-6 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
          >
            Check Availability
          </button>

        </div>
      </div>
  );
}
